import { useEffect, useState } from "react";
import {
  ArrowsHorizontal,
  CopySimple,
  X,
  ArrowClockwise,
  CheckCircle,
  Info,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtMoney } from "../../lib/finance-format";

/**
 * Reconciliation dialog — surfaces duplicates + potential transfers between the
 * user's own accounts. Buttons let the user mark each pair.
 */
export default function ReconciliationDialog({ open, onClose, scope, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [tab, setTab] = useState("duplicates");
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    if (open) scan();
  }, [open, scope]);

  const scan = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/finance/reconciliation/detect?scope=${scope || "all"}`);
      setDuplicates(data.duplicates || []);
      setTransfers(data.transfers || []);
    } catch (err) {
      toast.error("No se pudo detectar");
    } finally {
      setLoading(false);
    }
  };

  const applyAction = async (action, left_id, right_id, pairKey) => {
    setApplying(pairKey);
    try {
      await api.post("/finance/reconciliation/apply", { action, left_id, right_id });
      const label = action === "mark_duplicate" ? "Marcado como duplicado" : "Marcado como transferencia";
      toast.success(label);
      if (action === "mark_duplicate") {
        setDuplicates((prev) => prev.filter((d) => `${d.left.transaction_id}-${d.right.transaction_id}` !== pairKey));
      } else {
        setTransfers((prev) => prev.filter((t) => `${t.outflow.transaction_id}-${t.inflow.transaction_id}` !== pairKey));
      }
      onApplied?.();
    } catch (err) {
      toast.error("No se pudo aplicar");
    } finally {
      setApplying(null);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="reconciliation-dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-white/10 bg-[#0F1017] shadow-2xl flex flex-col"
      >
        <div className="p-5 border-b border-white/8 flex items-start justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#2563EB] flex items-center gap-1.5">
              <ArrowsHorizontal size={12} weight="bold" /> RECONCILIACIÓN · CFO
            </div>
            <div className="text-lg font-heading font-bold text-white mt-0.5">
              Cruzar transacciones
            </div>
            <div className="text-[11px] font-mono text-white/50 mt-1">
              Duplicados y transferencias entre tus cuentas propias.
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-white/10 text-white/50 hover:text-white flex items-center justify-center">
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="px-5 border-b border-white/8 flex items-center gap-1">
          {[
            { key: "duplicates", label: "Duplicados", count: duplicates.length, Icon: CopySimple },
            { key: "transfers", label: "Transferencias", count: transfers.length, Icon: ArrowsHorizontal },
          ].map((t) => {
            const active = tab === t.key;
            const IconComp = t.Icon;
            return (
              <button
                key={t.key}
                data-testid={`rec-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-2 px-4 py-3 text-[11px] font-mono uppercase tracking-widest font-bold transition border-b-2 ${
                  active ? "text-white border-[#2563EB]" : "text-white/50 border-transparent hover:text-white/80"
                }`}
              >
                <IconComp size={12} weight="bold" />
                {t.label}
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${active ? "bg-white/15" : "bg-white/8"}`}>
                  {t.count}
                </span>
              </button>
            );
          })}
          <div className="flex-1" />
          <button
            data-testid="rec-rescan"
            onClick={scan}
            disabled={loading}
            className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-white/60 hover:text-white"
          >
            <ArrowClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
            Re-scan
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="py-12 text-center text-xs font-mono text-white/40">
              <ArrowClockwise size={20} weight="bold" className="animate-spin mx-auto mb-2" />
              Analizando transacciones...
            </div>
          ) : tab === "duplicates" ? (
            duplicates.length === 0 ? (
              <EmptyState
                icon={<CheckCircle size={28} weight="fill" className="text-emerald-400/70" />}
                title="Sin duplicados"
                body="Tus transacciones están limpias."
              />
            ) : (
              duplicates.map((d) => {
                const pairKey = `${d.left.transaction_id}-${d.right.transaction_id}`;
                const busy = applying === pairKey;
                return (
                  <div
                    key={pairKey}
                    data-testid={`dup-${pairKey}`}
                    className="p-3 rounded-lg border border-white/8 bg-black/30"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-400 font-bold">
                        DUPLICADO · {Math.round(d.desc_similarity * 100)}% match
                      </span>
                      <span className="text-[10px] font-mono text-white/40">
                        {d.days_apart}d · {d.same_account ? "misma cuenta" : "cuentas distintas"}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <TxSnippet tx={d.left} />
                      <TxSnippet tx={d.right} />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => applyAction("mark_duplicate", d.left.transaction_id, d.right.transaction_id, pairKey)}
                        disabled={busy}
                        data-testid={`dup-merge-${pairKey}`}
                        className="px-3 py-1.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1.5"
                      >
                        {busy ? <ArrowClockwise size={11} weight="bold" className="animate-spin" /> : <CopySimple size={11} weight="bold" />}
                        Ocultar duplicado
                      </button>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            transfers.length === 0 ? (
              <EmptyState
                icon={<Info size={26} weight="fill" className="text-blue-400/70" />}
                title="Sin transferencias detectadas"
                body="No se encontraron movimientos que se cancelen entre tus cuentas."
              />
            ) : (
              transfers.map((tr) => {
                const pairKey = `${tr.outflow.transaction_id}-${tr.inflow.transaction_id}`;
                const busy = applying === pairKey;
                return (
                  <div
                    key={pairKey}
                    className="p-3 rounded-lg border border-blue-500/25 bg-blue-500/5"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-300 font-bold">
                        TRANSFERENCIA · ${fmtMoney(tr.amount)}
                      </span>
                      <span className="text-[10px] font-mono text-white/40">
                        {tr.days_apart}d {tr.keyword_match ? "· 🔑" : ""}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <TxSnippet tx={tr.outflow} tag="SALE" />
                      <TxSnippet tx={tr.inflow} tag="ENTRA" positive />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => applyAction("mark_transfer", tr.outflow.transaction_id, tr.inflow.transaction_id, pairKey)}
                        disabled={busy}
                        className="px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white font-heading font-bold text-xs flex items-center gap-1.5"
                      >
                        {busy ? <ArrowClockwise size={11} weight="bold" className="animate-spin" /> : <ArrowsHorizontal size={11} weight="bold" />}
                        Marcar transferencia
                      </button>
                    </div>
                  </div>
                );
              })
            )
          )}
        </div>
      </div>
    </div>
  );
}

function TxSnippet({ tx, tag, positive }) {
  return (
    <div className="p-2 rounded-lg bg-black/40 border border-white/6">
      <div className="flex items-center gap-2 mb-0.5">
        {tag && (
          <span className={`text-[8px] font-mono uppercase tracking-widest px-1 py-0.5 rounded ${
            positive ? "bg-emerald-500/20 text-emerald-400" : "bg-red-500/20 text-red-400"
          }`}>
            {tag}
          </span>
        )}
        <span className="text-[10px] font-mono text-white/40 tabular-nums">{tx.date}</span>
      </div>
      <div className="text-xs font-heading font-bold text-white truncate">
        {tx.description}
      </div>
      <div className="mt-1 flex items-center justify-between">
        <span className="text-[10px] font-mono text-white/40">{tx.category}</span>
        <span className={`text-sm font-mono font-bold tabular-nums ${tx.amount >= 0 ? "text-emerald-400" : "text-white"}`}>
          {tx.amount >= 0 ? "+" : ""}{fmtMoney(tx.amount)}
        </span>
      </div>
    </div>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div className="py-12 text-center">
      <div className="inline-flex mx-auto mb-2">{icon}</div>
      <div className="text-sm font-heading font-bold text-white mb-1">{title}</div>
      <div className="text-[11px] font-mono text-white/50">{body}</div>
    </div>
  );
}
